import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Bookmark } from "lucide-react";

const PinContext = createContext({
  ids: { member: [], business: [], post: [], event: [] },
  toggle: () => {},
  isPinned: () => false,
});

export const PinProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState({ member: [], business: [], post: [], event: [] });

  const refresh = useCallback(async () => {
    if (!user) {
      setIds({ member: [], business: [], post: [], event: [] });
      return;
    }
    try {
      const res = await axios.get(`${API}/pins/ids`);
      setIds(res.data);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (target_type, target_id) => {
    if (!user) return;
    // Optimistic
    setIds((prev) => {
      const list = prev[target_type] || [];
      const next = list.includes(target_id) ? list.filter((x) => x !== target_id) : [...list, target_id];
      return { ...prev, [target_type]: next };
    });
    try {
      await axios.post(`${API}/pins/toggle`, { target_type, target_id });
    } catch { refresh(); }
  };

  const isPinned = (target_type, target_id) => (ids[target_type] || []).includes(target_id);

  return (
    <PinContext.Provider value={{ ids, toggle, isPinned, refresh }}>{children}</PinContext.Provider>
  );
};

export const usePins = () => useContext(PinContext);

export const PinButton = ({ targetType, targetId, label = "", className = "", onLight = false }) => {
  const { user } = useAuth();
  const { isPinned, toggle } = usePins();
  if (!user) return null;
  const pinned = isPinned(targetType, targetId);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(targetType, targetId); }}
      title={pinned ? "Unpin from My Portal" : "Save to My Portal"}
      data-testid={`pin-${targetType}-${targetId}`}
      className={`inline-flex items-center justify-center gap-1.5 transition-colors ${className} ${
        pinned
          ? "bg-[#FF2D87] text-white"
          : onLight
            ? "bg-white text-[#0A0A0A] hover:bg-[#FF2D87] hover:text-white border border-[#0A0A0A]"
            : "bg-[#0A0A0A] text-white hover:bg-[#FF2D87] border border-white/30"
      }`}
    >
      <Bookmark className={`w-4 h-4 ${pinned ? "fill-current" : ""}`} />
      {label}
    </button>
  );
};
